import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { Conversation, Message, MessageStatus as Status } from './types';

export function ContactAvatar({
  initials,
  hue,
  small = false,
}: {
  initials: string;
  hue: number;
  small?: boolean;
}) {
  return (
    <span
      className={`avatar ${small ? 'avatar--small' : ''}`}
      style={{ '--avatar-hue': hue } as React.CSSProperties}
    >
      {initials}
    </span>
  );
}
export function MessageStatus({ status }: { status: Status | undefined }) {
  const labels: Record<Status, string> = {
    pending: 'Oczekuje',
    sent: 'Wysłano',
    delivered: 'Dostarczono',
    read: 'Odczytano',
    error: 'Błąd — kliknij, aby ponowić',
  };
  if (!status) return null;
  return (
    <span
      className={`message-status message-status--${status}`}
      aria-label={labels[status]}
      title={labels[status]}
    >
      {status === 'pending' ? '◷' : status === 'error' ? '!' : status === 'sent' ? '✓' : '✓✓'}
    </span>
  );
}
export function ConversationListItem({
  conversation,
  active,
  onSelect,
}: {
  conversation: Conversation;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button className={`conversation-row ${active ? 'is-active' : ''}`} onClick={onSelect}>
      <ContactAvatar initials={conversation.initials} hue={conversation.avatarHue} />
      <span className="conversation-copy">
        <span className="conversation-top">
          <strong>{conversation.name}</strong>
          <time>{conversation.time}</time>
        </span>
        <span className="conversation-bottom">
          <span>{conversation.lastMessage}</span>
          {conversation.unread > 0 && <b className="unread-badge">{conversation.unread}</b>}
        </span>
      </span>
    </button>
  );
}
export function ConversationList({
  conversations,
  activeId,
  onSelect,
}: {
  conversations: Conversation[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  const [query, setQuery] = useState('');
  const shown = conversations.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));
  return (
    <aside className="sidebar">
      <header className="brand-header">
        <span className="brand-mark" aria-hidden>
          ●—●
        </span>
        <span>
          <b>Link</b>
          <small>Prywatnie. Blisko.</small>
        </span>
        <button className="icon-button" aria-label="Nowa rozmowa">
          ＋
        </button>
      </header>
      <div className="search">
        <span>⌕</span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Szukaj rozmowy"
          aria-label="Szukaj rozmowy"
        />
      </div>
      <nav className="conversation-list" aria-label="Rozmowy">
        {shown.map((c) => (
          <ConversationListItem
            key={c.id}
            conversation={c}
            active={c.id === activeId}
            onSelect={() => onSelect(c.id)}
          />
        ))}
        {shown.length === 0 && (
          <div className="empty-state">
            <b>Brak wyników</b>
            <span>Spróbuj innej nazwy.</span>
          </div>
        )}
      </nav>
      <MobileNavigation />
    </aside>
  );
}
export function MobileNavigation() {
  return (
    <nav className="mobile-nav" aria-label="Główna nawigacja">
      <button className="active">
        ◉<span>Rozmowy</span>
      </button>
      <button>
        ♙<span>Kontakty</span>
      </button>
      <button>
        ⚙<span>Ustawienia</span>
      </button>
    </nav>
  );
}
export function ChatHeader({
  conversation,
  onBack,
}: {
  conversation: Conversation;
  onBack: () => void;
}) {
  return (
    <header className="chat-header">
      <button className="icon-button back-button" onClick={onBack} aria-label="Wróć do rozmów">
        ‹
      </button>
      <ContactAvatar initials={conversation.initials} hue={conversation.avatarHue} small />
      <span className="chat-person">
        <strong>{conversation.name}</strong>
        <small>{conversation.status}</small>
      </span>
      <button
        className="icon-button future"
        aria-label="Połączenia audio — funkcja w przygotowaniu"
        title="Wkrótce"
      >
        ⌕
      </button>
      <button className="icon-button" aria-label="Menu rozmowy">
        •••
      </button>
    </header>
  );
}
function DateSeparator() {
  return (
    <div className="date-separator">
      <span>Dzisiaj</span>
    </div>
  );
}
export function MessageBubble({ message, grouped }: { message: Message; grouped: boolean }) {
  return (
    <div className={`message-line message-line--${message.author} ${grouped ? 'is-grouped' : ''}`}>
      <div className={`bubble ${message.status === 'error' ? 'bubble--error' : ''}`}>
        {message.replyTo && (
          <div className="reply-preview">
            <b>Odpowiedź</b>
            <span>{message.replyTo}</span>
          </div>
        )}
        <span className="message-text">
          {message.deleted ? 'Wiadomość usunięta lokalnie' : message.text}
        </span>
        <span className="message-meta">
          <time>{message.timestamp}</time>
          <MessageStatus status={message.status} />
        </span>
      </div>
    </div>
  );
}
export function TypingIndicator() {
  return (
    <div className="message-line message-line--contact">
      <div className="typing" aria-label="Kontakt pisze">
        <i />
        <i />
        <i />
      </div>
    </div>
  );
}
export function MessageList({ conversation }: { conversation: Conversation }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = ref.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [conversation.id]);
  return (
    <div className="messages" ref={ref}>
      <div className="privacy-pill">Wiadomości w demo pozostają wyłącznie w tej przeglądarce</div>
      <DateSeparator />
      {conversation.messages.map((message, index) => {
        const previous = conversation.messages[index - 1];
        const grouped = previous?.author === message.author && !message.replyTo;
        return <MessageBubble key={message.id} message={message} grouped={grouped} />;
      })}
      {conversation.typing && <TypingIndicator />}
    </div>
  );
}
export function MessageComposer({ onSend }: { onSend: (text: string) => void }) {
  const [text, setText] = useState('');
  const send = () => {
    const clean = text.trim();
    if (!clean) return;
    onSend(clean);
    setText('');
  };
  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !('ontouchstart' in window)) {
      event.preventDefault();
      send();
    }
  };
  return (
    <footer className="composer">
      <button className="composer-action" aria-label="Dodaj załącznik — wkrótce" title="Wkrótce">
        ＋
      </button>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={keyDown}
        rows={1}
        placeholder="Napisz wiadomość"
        aria-label="Treść wiadomości"
      />
      <button
        className="send-button"
        onClick={send}
        disabled={!text.trim()}
        aria-label="Wyślij wiadomość"
      >
        ➤
      </button>
    </footer>
  );
}
export function ChatView({
  conversation,
  onBack,
  onSend,
}: {
  conversation: Conversation;
  onBack: () => void;
  onSend: (text: string) => void;
}) {
  return (
    <section className="chat">
      <ChatHeader conversation={conversation} onBack={onBack} />
      <MessageList conversation={conversation} />
      <MessageComposer onSend={onSend} />
    </section>
  );
}
